import { lazy, Suspense } from 'react';
import { Card, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { type DataCardProps } from '../types/props';

import { useStatsSummary } from '../queries/useStatsSummary';
import QueryErrorState from '../components/QueryErrorState';
import { RARITY_VALUES, RARITY_COLORS, DOCUMENT_TITLES } from '../constants';
import { useDocumentTitle } from '../hooks';
import styles from './DashboardView.module.css';

const DashboardChartCard = lazy(() => import('./DashboardChartCard'));

const LoadingIcon = <LoadingOutlined className="sa-loading-icon" spin />;

const DataCard = ({ name, value }: DataCardProps) => (
  <div className={styles.dashboardCard} data-testid="sa-dashboard-card">
    <Card title={name} data-testid="sa-dashboard-card-content">
      {value}
    </Card>
  </div>
);

const DashboardView = () => {
  const { data, isLoading, isError, error, refetch } = useStatsSummary();

  const { lastSnapshot, chartData } = data ?? { lastSnapshot: { data: {} }, chartData: [] };

  const { players = 0, players24h = 0, cards = 0, cards24h = 0 } = lastSnapshot?.data || {};

  useDocumentTitle(DOCUMENT_TITLES.DASHBOARD);

  const formattedDate = lastSnapshot?.updated
    ? new Date(lastSnapshot.updated).toDateString()
    : 'No data';

  if (isError) {
    return <QueryErrorState error={error} onRetry={refetch} />;
  }

  return (
    <Spin indicator={LoadingIcon} spinning={isLoading} data-testid="sa-dashboard-spin">
      <div className={styles.dashboardView} data-testid="sa-dashboard-view">
        <div className={styles.dashboardCards} data-testid="sa-dashboard-cards">
          <DataCard
            name="Last Update Date"
            value={<span data-testid="sa-last-update-date">{formattedDate}</span>}
          />
          <DataCard
            name="Total Players"
            value={<span data-testid="sa-total-players">{players}</span>}
          />
          <DataCard
            name="New Players in 24h"
            value={<span data-testid="sa-new-players-24h">{players24h}</span>}
          />
          <DataCard name="Total Cards" value={<span data-testid="sa-total-cards">{cards}</span>} />
          <DataCard
            name="New Cards in 24h"
            value={<span data-testid="sa-new-cards-24h">{cards24h}</span>}
          />
        </div>
        <div className={styles.dashboardCharts} data-testid="sa-dashboard-charts">
          <Suspense fallback={<Spin />}>
            <DashboardChartCard
              name="Number of Cards and Players"
              data={chartData}
              xAxisName="date"
              yAxisName="cards"
              yAxisRightName="players"
            />
          </Suspense>
        </div>
        <div className={styles.dashboardCharts} data-testid="sa-dashboard-charts-new">
          <Suspense fallback={<Spin />}>
            <DashboardChartCard
              name="New Cards and Players"
              data={chartData}
              xAxisName="date"
              yAxisName="cards24h"
              yAxisRightName="players24h"
            />
          </Suspense>
        </div>
        <div className={styles.dashboardCharts} data-testid="sa-dashboard-charts-rarity-1">
          <Suspense fallback={<Spin />}>
            <DashboardChartCard
              name="New Cards by rarity"
              data={chartData}
              xAxisName="date"
              yAxisName={RARITY_VALUES.LIMITED}
              yAxisRightName={RARITY_VALUES.RARE}
              yAxisColor={RARITY_COLORS[RARITY_VALUES.LIMITED]}
              yAxisRightColor={RARITY_COLORS[RARITY_VALUES.RARE]}
            />
          </Suspense>
        </div>
        <div className={styles.dashboardCharts} data-testid="sa-dashboard-charts-rarity-2">
          <Suspense fallback={<Spin />}>
            <DashboardChartCard
              name="New Cards by rarity"
              data={chartData}
              xAxisName="date"
              yAxisName={RARITY_VALUES.SUPER_RARE}
              yAxisRightName={RARITY_VALUES.UNIQUE}
              yAxisColor={RARITY_COLORS[RARITY_VALUES.SUPER_RARE]}
              yAxisRightColor={RARITY_COLORS[RARITY_VALUES.UNIQUE]}
            />
          </Suspense>
        </div>
      </div>
    </Spin>
  );
};

export default DashboardView;
